// ── Welle A3: Bausteine eines Beitrags anzeigen ──
// Kein HTML: Text wird als React-Elemente gesetzt (nie dangerouslySetInnerHTML). Erlaubt sind
// **fett**, Listen mit „- “ und ==Markierungen==; Markierungen bekommen über den ganzen Beitrag
// fortlaufende Nummern (in Lese-Reihenfolge), der Baustein „Hinweise“ erklärt sie der Reihe nach.
// Bilder kommen nur mit Anmeldung (Token) und werden als Blob-URL gezeigt.
import React, { useEffect, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { API_BASE, apiHeaders } from '../../lib/api';
import { AKTION_LABEL } from './typen';
import type { Aktion, Baustein } from './typen';

type Zaehler = { n: number };
const istHttps = (u: string) => { try { return new URL(u).protocol === 'https:'; } catch { return false; } };

/** Eine Zeile: **fett** und ==Markierung== */
function zeile(text: string, z: Zaehler, key: string): React.ReactNode[] {
  const teile: React.ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|==([^=]+)==)/g;
  let letzt = 0, m: RegExpExecArray | null, i = 0;
  while ((m = re.exec(text))) {
    if (m.index > letzt) teile.push(text.slice(letzt, m.index));
    if (m[2] != null) teile.push(<strong key={`${key}-${i++}`}>{m[2]}</strong>);
    else {
      const nr = ++z.n;
      teile.push(<span key={`${key}-${i++}`} className="ld-si-mark"><mark>{m[3]}</mark><span className="ld-si-nr" aria-label={`Hinweis ${nr}`}>{nr}</span></span>);
    }
    letzt = m.index + m[0].length;
  }
  if (letzt < text.length) teile.push(text.slice(letzt));
  return teile;
}

/** Absätze (Leerzeile), Listen („- “), Zeilenumbrüche */
export function TextBlock({ text, z }: { text: string; z: Zaehler }) {
  const absaetze = text.split(/\n\s*\n/).map(a => a.trim()).filter(Boolean);
  return (
    <>
      {absaetze.map((a, ai) => {
        const zeilen = a.split('\n');
        if (zeilen.every(l => /^\s*-\s+/.test(l))) {
          return <ul key={ai} className="ld-si-liste">{zeilen.map((l, li) => <li key={li}>{zeile(l.replace(/^\s*-\s+/, ''), z, `${ai}-${li}`)}</li>)}</ul>;
        }
        return <p key={ai}>{zeilen.map((l, li) => <React.Fragment key={li}>{li > 0 && <br />}{zeile(l, z, `${ai}-${li}`)}</React.Fragment>)}</p>;
      })}
    </>
  );
}

/** Bild mit Anmeldung laden (Blob-URL, beim Verlassen freigeben) */
export function useBild(pfad: string | null) {
  const [url, setUrl] = useState<string | null>(null);
  const [fehler, setFehler] = useState(false);
  useEffect(() => {
    if (!pfad) return;
    let aktiv = true, u: string | null = null;
    setFehler(false);
    fetch(`${API_BASE}${pfad}`, { headers: apiHeaders() })
      .then(r => r.ok ? r.blob() : Promise.reject(r.status))
      .then(b => { if (!aktiv) return; u = URL.createObjectURL(b); setUrl(u); })
      .catch(() => { if (aktiv) setFehler(true); });
    return () => { aktiv = false; if (u) URL.revokeObjectURL(u); setUrl(null); };
  }, [pfad]);
  return { url, fehler };
}
function Bild({ pfad, alt }: { pfad: string; alt: string }) {
  const { url, fehler } = useBild(pfad);
  if (fehler) return <div className="ld-si-bild ist-leer"><MIcon name="image_not_supported" size={22} /></div>;
  return url ? <img className="ld-si-bild" src={url} alt={alt} /> : <div className="ld-si-bild ist-leer" aria-busy="true" />;
}

export function Bausteine({ bausteine, bildPfad, onAktion, ernstfall = false }: {
  bausteine: Baustein[]; bildPfad: (id: number) => string; onAktion?: (a: Aktion) => void; ernstfall?: boolean;
}) {
  const z: Zaehler = { n: 0 };
  let hinweisNr = 0;
  return (
    <div className="ld-si-bausteine">
      {bausteine.map((b, i) => {
        switch (b.typ) {
          case 'text':
            return <section key={i} className="ld-si-text">{b.titel && <h3>{b.titel}</h3>}<TextBlock text={b.text} z={z} /></section>;
          case 'beispiel':
            return (
              <section key={i} className="ld-si-beispiel-wrap">
                {b.titel && <h3>{b.titel}</h3>}
                <div className="ld-si-beispiel">
                  {b.bild_id != null && <Bild pfad={bildPfad(b.bild_id)} alt="Beispiel" />}
                  {b.text && <TextBlock text={b.text} z={z} />}
                </div>
              </section>
            );
          case 'bild':
            return <figure key={i} className="ld-si-figur"><Bild pfad={bildPfad(b.bild_id)} alt={b.alt ?? ''} />{b.alt && <figcaption>{b.alt}</figcaption>}</figure>;
          case 'hinweise':
            return (
              <section key={i} className="ld-si-hinweise">
                {b.titel && <h3>{b.titel}</h3>}
                <ol>{b.punkte.map((p, pi) => { const nr = ++hinweisNr; return <li key={pi}><span className="ld-si-nr" aria-hidden="true">{nr}</span><span>{p.text}</span></li>; })}</ol>
              </section>
            );
          case 'hilft':
            return <aside key={i} className="ld-si-hilft"><b><MIcon name="shield" size={17} />So hilft dir LehrerDesk</b><TextBlock text={b.text} z={z} /></aside>;
          case 'checkliste':
            return (
              <section key={i} className="ld-si-check-liste">
                {b.titel && <h3>{b.titel}</h3>}
                <ul>{b.punkte.map((p, pi) => <li key={pi}><MIcon name="check_circle" size={18} /><span>{p.text}</span></li>)}</ul>
              </section>
            );
          case 'schritte':
            return (
              <section key={i} className={`ld-si-schritte${ernstfall ? ' ist-ernstfall' : ''}`}>
                {b.titel && <h3>{b.titel}</h3>}
                <ol>
                  {b.schritte.map((s, si) => (
                    <li key={si}>
                      <span className="ld-si-schritt-nr" aria-hidden="true">{si + 1}</span>
                      <div>
                        <b>{s.titel}</b>
                        {s.text && <p>{s.text}</p>}
                        {s.aktion && AKTION_LABEL[s.aktion] && (
                          <button type="button" className={`ld-si-aktion${s.aktion === 'ueberall_abmelden' ? ' ist-kritisch' : ''}`} onClick={() => onAktion?.(s.aktion!)} disabled={!onAktion}>
                            <MIcon name={AKTION_LABEL[s.aktion].icon} size={17} />{AKTION_LABEL[s.aktion].label}
                          </button>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            );
          case 'link':
            if (!istHttps(b.url)) return null;
            return (
              <a key={i} className="ld-si-link" href={b.url} target="_blank" rel="noopener noreferrer nofollow">
                <MIcon name="open_in_new" size={17} />{b.titel}
              </a>
            );
          default:
            return null;
        }
      })}
    </div>
  );
}
